import { useEffect, useRef } from 'preact/hooks';

import type { TemplateFunction, TemplateResult } from 'Components/types';
import { vehicleSelectionSelector } from 'Core/selectors/fitmentSearch/index';
import { searchRequestSelector, totalHitsSelector } from 'Core/selectors/search';
import { viewSelector } from 'Core/selectors/show';
import { useSelector } from 'Core/store';
import type {
  default as SearchItemData,
  SearchItemTemplateParams as BaseSearchItemTemplateParams,
} from 'Models/searchItemData';
import requestConfig from 'Models/uiConfig/requestConfig';
import { track } from 'Modules/serverApi/index';
import { cloneSafe } from 'Utils/components';
import { observeVisibility } from 'Utils/intersectionObserver';
import safeTemplateCall from 'Utils/safeTemplateCall';

interface Props {
  template: TemplateFunction<SearchItemTemplateParams>;
  item: SearchItemData;
}

export type SearchItemTemplateParams = BaseSearchItemTemplateParams<{
  position: number;
  view: string;
}>;

export default function SearchItem({ template, item }: Props): TemplateResult {
  const view = useSelector(viewSelector);
  const searchTotalHits = useSelector(totalHitsSelector);
  const searchRequest = useSelector(searchRequestSelector);

  const rootRef = useRef<HTMLElement | null>(null);

  const vehicleSelection = useSelector(vehicleSelectionSelector);
  const params = item.getParams(vehicleSelection);

  const isTargetInItemLink = (target: EventTarget | null) => {
    const linkTarget = target instanceof Element ? target : null;
    const link = linkTarget?.closest<HTMLAnchorElement>('a[href]:not([data-cm-prevent-redirect])');
    if (link) {
      const { origin } = window.location;

      const targetUrl = new URL(link.href, origin);
      const itemUrl = new URL(params.url as string, origin);

      return targetUrl.origin === itemUrl.origin && targetUrl.pathname === itemUrl.pathname;
    }
    return false;
  };

  const position = item.position ?? -1;

  useEffect(() => {
    const element = rootRef.current;

    if (!item.id || !element) {
      return;
    }

    return observeVisibility(element, () => {
      if (requestConfig.isAnalyticsEnabled()) {
        track('ProductImpression', {
          item,
          position,
          searchRequest,
        });
      }
    });
  }, [position, searchRequest?.pageNumber, searchRequest?.pageSize, searchRequest, item]);

  const component = safeTemplateCall(
    template,
    { ...params, view, position },
    'SearchItem',
    item.id as string,
  );
  return cloneSafe(component, rootRef, {
    onMouseUp: (e) => {
      if (
        requestConfig.isAnalyticsEnabled() &&
        (e.button === 0 || e.button === 1) &&
        isTargetInItemLink(e.target)
      ) {
        track('ClickOnSearchResult', { item, position, searchTotalHits });
      }
    },
  });
}
